import { type GetStoreState, type SetStoreState, createStore } from '@/stores/createStore';
import type { AgentBuilderGraph, AgentBuilderVersion } from '@/agentBuilder/agentBuilder.types';

export interface AgentBuilderState {
  /** The currently loaded agent builder graph, or undefined while loading or not yet loaded. */
  currentAgentBuilder: AgentBuilderGraph | undefined;
  /** ID of the node selected in the inspector panel, or undefined if none. */
  selectedNodeId: string | undefined;
  /** True when the editor has unsaved local changes vs. the loaded server state. */
  isDirty: boolean;
  /** Published versions of the current agent builder, newest first. */
  versions: AgentBuilderVersion[];
  /** ID of the version the user is viewing/editing, or null if no version is selected. */
  selectedVersionId: string | null;
  /** ID of the version the current draft was forked from, or null if not a draft. */
  draftBaseVersionId: string | null;
  /** True while the versions list is being fetched. */
  versionsLoading: boolean;
}

const initialState: AgentBuilderState = {
  currentAgentBuilder: undefined,
  selectedNodeId: undefined,
  isDirty: false,
  versions: [],
  selectedVersionId: null,
  draftBaseVersionId: null,
  versionsLoading: false,
};

const agentBuilderActionsFactory = (set: SetStoreState<AgentBuilderState>, _get: GetStoreState<AgentBuilderState>) => ({
  /**
   * Replace the currently loaded agent builder. Atomically updates `isDirty` to match
   * the incoming graph's `meta.hasDraft` flag — preserves the coupling enforced by
   * the legacy `AGENT_BUILDER_SET_CURRENT` handler.
   */
  setCurrentAgentBuilder: (agentBuilder: AgentBuilderGraph | undefined) =>
    set((draft) => {
      draft.currentAgentBuilder = agentBuilder;
      draft.isDirty = agentBuilder?.meta?.hasDraft ?? false;
    }),

  setSelectedNode: (nodeId: string | undefined) =>
    set((draft) => {
      draft.selectedNodeId = nodeId;
    }),

  /** Patch the loaded agent builder's name. No-op if no agent builder is loaded. */
  setAgentBuilderName: (name: string) =>
    set((draft) => {
      if (draft.currentAgentBuilder) {
        draft.currentAgentBuilder.name = name;
      }
    }),

  markAgentBuilderClean: () =>
    set((draft) => {
      draft.isDirty = false;
    }),

  markAgentBuilderDirty: () =>
    set((draft) => {
      draft.isDirty = true;
    }),

  /**
   * Replace the versions list. Atomically clears `versionsLoading` — preserves the
   * coupling enforced by the legacy `AGENT_BUILDER_SET_VERSIONS` handler.
   */
  setAgentBuilderVersions: (versions: AgentBuilderVersion[]) =>
    set((draft) => {
      draft.versions = versions;
      draft.versionsLoading = false;
    }),

  setAgentBuilderVersionsLoading: (loading: boolean) =>
    set((draft) => {
      draft.versionsLoading = loading;
    }),

  setAgentBuilderSelectedVersion: (versionId: string | null) =>
    set((draft) => {
      draft.selectedVersionId = versionId;
    }),

  setAgentBuilderDraftBaseVersion: (versionId: string | null) =>
    set((draft) => {
      draft.draftBaseVersionId = versionId;
    }),

  /** Full state wipe to initial values. Fired on AgentBuilder.page unmount. */
  resetAgentBuilder: () => set(() => structuredClone(initialState)),
});

const [AgentBuilderStoreProvider, useStore, useSelector] = createStore(
  'AgentBuilder',
  initialState,
  agentBuilderActionsFactory,
);

export { AgentBuilderStoreProvider };

export const useCurrentAgentBuilder = () => useSelector((s) => s.currentAgentBuilder);
export const useSelectedNodeId = () => useSelector((s) => s.selectedNodeId);
export const useIsDirty = () => useSelector((s) => s.isDirty);
export const useVersions = () => useSelector((s) => s.versions);
export const useSelectedVersionId = () => useSelector((s) => s.selectedVersionId);
export const useDraftBaseVersionId = () => useSelector((s) => s.draftBaseVersionId);
export const useVersionsLoading = () => useSelector((s) => s.versionsLoading);

/** Stable actions handle — never causes re-renders. */
export const useAgentBuilderActions = () => useStore().actions;
